import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Radio,
  RadioGroup,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Collapse,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import TagIcon from "@mui/icons-material/Tag";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import PaymentsIcon from "@mui/icons-material/Payments";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

/* ─── shared design tokens ─────────────────────────────────── */
const CARD_RADIUS = "16px";
const SECTION_GAP = "20px";

// Master interest / insurance percentages can't exceed the government-approved
// official cap (or go negative). Mirrors OFFICIAL_RATE_CAP on the backend.
const MAX_MASTER_RATE = 1.16;
const clampRate = (n) => Math.min(Math.max(n, 0), MAX_MASTER_RATE);

/* ─── payment / receipt option explanations ─────────────────────
   Each rule gets a small info icon beside it: hover shows "Know more",
   clicking opens a dialog with the detailed description below. */
const SETTING_INFO = {
  use_prefix_for_ticket: {
    title: "Use prefix in ticket",
    description:
      "When on, the pawn ticket sequence numbers will be prepended with the entered prefix.",
  },
  OTP_for_customer_creation: {
    title: "OTP validation for customer creation",
    description:
      "When on, an OTP will be sent to the mobile number entered as the customer's mobile number while creating a customer. Upon verification of the OTP only, that customer will be created.",
  },
  allow_duplicate_mobile: {
    title: "Allow duplicate mobile numbers",
    description:
      "Off by default: a mobile number can belong to only one customer, which catches the same person being entered twice. Turn it on when your customers genuinely share phones — a whole family on one number is common — and the app stops refusing a repeated number. It never saves silently: creating such a customer asks you to confirm it really is a different person, and the bulk import offers a switch for those rows. Identify people by their Aadhar or PAN after that: the lookup and the payment screen's search show both, and the bulk import's Parent Aadhar column uses the Aadhar to say which of two people sharing a number a nominee belongs to. Nothing already saved changes when you switch this off — existing duplicates stay, you simply cannot add more.",
  },
  manual_voucher_no: {
    title: "Manual voucher number",
    description:
      "When on, the voucher number on the payment screen becomes editable while creating a new payment, so you can enter any voucher number you want instead of the auto-generated one. As you type, the number is checked against existing loans — if it is already used, the field turns red and Save/Print are disabled until you pick a free number. This applies only when creating; an existing payment's voucher number can never be changed.",
  },
  one_month_deduct: {
    title: "One month deduct",
    description:
      "Deducts one month's interest from the payment amount up front. Because that first month's interest is collected at payout time, when the customer later comes to pay a receipt against the loan they need not pay interest for the first month.",
  },
  no_interest_upto_2_days: {
    title: "No interest within 2 days",
    description:
      "Grants a 2-day grace period when the customer fails to deposit the monthly interest due in time. For day-wise interest, the same grace applies if the last receipt or payment was made within 2 days of the current day.",
  },
  pawn_monthly_interest: {
    title: "Pawn monthly interest",
    description:
      "Calculates interest on a monthly basis. When turned off, interest is charged day-wise (per day) instead of per whole month.",
  },
  pawn_compound_interest: {
    title: "Pawn compound interest",
    description:
      "Implements compound interest: after a tenure of one year, any leftover interest dues are added to the remaining principal, and interest thereafter is charged on that increased principal.",
  },
  less_than_15_days_as_half_month: {
    title: "< 15 days = half month",
    description:
      "Charges interest for half of the current calendar month when a receipt is made any day within 15 days of the last receipt paid — or of the day the loan was taken, if no receipt has been made yet.",
  },
  OTP_for_settlement: {
    title: "OTP validation for settlement",
    description:
      "Turning it on would ask for an OTP as validation for a settlement, sent to the mobile number of that customer. The loan is only closed after the OTP is verified; turning it off settles the loan directly without an OTP step.",
  },
};

/* ─── small "know more" info icon ───────────────────────────── */
const InfoDot = ({ onClick, hoverColor = "#2563EB" }) => (
  <Tooltip title="Know more" arrow placement="right">
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        p: "2px",
        ml: 0.2,
        color: "#94a3b8",
        "&:hover": { color: hoverColor, bgcolor: `${hoverColor}12` },
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 15 }} />
    </IconButton>
  </Tooltip>
);

/* ─── discreet scrollbar for a panel that scrolls inside itself ───
   The settings screen hands each section a fixed quadrant of the viewport, so
   the taller sections scroll internally. Report tables hide their scrollbar so
   they read as part of the page — but a settings panel is a form, and a form
   with more fields below the fold needs to say so. Hence thin and pale rather
   than absent. Cosmetics only: the `overflow` that switches it on is applied
   per breakpoint by the caller. */
const PANEL_SCROLL_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "#cbd5e1 transparent",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "#dbe1ea",
    borderRadius: 3,
  },
  "&:hover::-webkit-scrollbar-thumb": { background: "#c3ccd8" },
};

/* ─── tiny section-card wrapper ─────────────────────────────────
   `fill` = this card IS a settings quadrant: it takes the whole height it was
   handed and its body scrolls, so the page itself never has to.

   Only from `lg` up, though. `fill` comes from a prop, which knows nothing
   about the screen — and below lg the settings page abandons the quadrants for
   a stacked, page-scrolling column. Left unconditional, a phone got four cards
   each clamped to about 90px with the rest of the form hidden behind an inner
   scrollbar. Hence every rule here is breakpoint-scoped.

   Off entirely for the nested sub-cards inside the rules section, which always
   size to their content. */
const SectionCard = ({
  icon,
  title,
  accentColor = "#2563EB",
  children,
  badge,
  fill = false,
}) => (
  <Box
    sx={{
      bgcolor: "#ffffff",
      borderRadius: CARD_RADIUS,
      border: "1px solid #e8eaf0",
      overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      ...(fill
        ? {
            height: { xs: "auto", lg: "100%" },
            minHeight: 0,
            display: { xs: "block", lg: "flex" },
            flexDirection: "column",
          }
        : {}),
    }}
  >
    {/* Card header strip */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        py: 1.6,
        borderBottom: "1px solid #f0f2f5",
        bgcolor: "#fafbfc",
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            bgcolor: `${accentColor}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: accentColor,
            "& svg": { fontSize: 17 },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: "#1a1a2e",
            fontSize: "0.88rem",
            letterSpacing: "0.1px",
          }}
        >
          {title}
        </Typography>
      </Box>
      {badge}
    </Box>

    {/* Card body */}
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 2.5 },
        ...(fill
          ? {
              flex: 1,
              minHeight: 0,
              overflowY: { xs: "visible", lg: "auto" },
              overflowX: "hidden",
              ...PANEL_SCROLL_SX,
            }
          : {}),
      }}
    >
      {children}
    </Box>
  </Box>
);

/* ─── styled checkbox ────────────────────────────────────────── */
const StyledCheck = ({ checked, onChange, label, disabled }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        size="small"
        sx={{
          color: "#c0c4cc",
          "&.Mui-checked": { color: "#2563EB" },
          p: "4px",
          mr: "4px",
        }}
      />
    }
    label={
      <Typography
        variant="body2"
        sx={{ fontSize: "0.83rem", color: disabled ? "#b0b7c3" : "#374151" }}
      >
        {label}
      </Typography>
    }
    sx={{ m: 0, alignItems: "center", userSelect: "none" }}
  />
);

/* ─── main component ─────────────────────────────────────────────
   `section` picks ONE of the three cards to render:
     "general"  Global Master      "accounts"  Account Mapping
     "rules"    Payment, Receipt & Settlement Rules
     "all"      all three stacked (the original single-column layout)

   The settings screen lays its sections out as four quadrants of one
   non-scrolling page, so each has to be mountable on its own. A prop rather
   than three files: the cards share the field styling, the account
   autocomplete helpers and the "know more" dialog, and splitting them would
   mean either duplicating that or threading it back through anyway. */
const PawnConfiguration = ({
  section = "all",
  fetching = false,
  settings,
  onSettingsChange,
  accounts = [],
  accountsLoading = false,
  currentStatus = 1,
  currentFyId = null,
  // The year the calendar is in, for the "Current" chip. Distinct from
  // currentFyId, which is the year this branch has selected.
  calendarFyId = null,
  fyOptions = [],
  fyLoading = false,
  onFyChange = () => {},
  prefixPeek = null,
  resetSequence = false,
  onResetSequenceChange = () => {},
}) => {
  const isUnofficial = currentStatus === 2;

  /* ── "know more" info dialog ── */
  const [infoKey, setInfoKey] = useState(null);
  const info = infoKey ? SETTING_INFO[infoKey] : null;

  /* ── account autocomplete helpers ── */
  const getAccountOptions = () =>
    accounts.map((a) => ({
      id: a.account_id,
      label: a.account_code
        ? `${a.account_code} — ${a.account_name}`
        : a.account_name,
    }));

  const getSelectedAccount = (fieldValue) => {
    if (!fieldValue) return null;
    const a = accounts.find((acc) => acc.account_id === fieldValue);
    if (!a) return null;
    return {
      id: a.account_id,
      label: a.account_code
        ? `${a.account_code} — ${a.account_name}`
        : a.account_name,
    };
  };

  const handleAutocompleteChange = (fieldName) => (_, newValue) =>
    onSettingsChange({
      ...settings,
      [fieldName]: newValue ? newValue.id : null,
    });

  /* ── shared textfield sx ── */
  const tfSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      fontSize: "0.875rem",
      bgcolor: "#fafbfc",
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a5b4fc" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#2563EB",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#2563EB" },
    "& .MuiFormHelperText-root": { fontSize: "0.72rem" },
  };

  /* ── which card(s) this instance renders ── */
  // A single-section instance IS a quadrant: it fills the height it was handed
  // and scrolls inside itself. "all" keeps the original stacked column.
  const single = section !== "all";
  const show = (key) => !single || section === key;

  // Grid widths were tuned for the old full-width column. A quadrant is about
  // half that wide, but `md`/`lg` key off the VIEWPORT — so the same fractions
  // survive into a container half the size and squeeze a field to 80px. `col`
  // picks the fraction for the layout actually in use: col(full, quadrant).
  const col = (full, quadrant) => (single ? quadrant : full);

  /* ── loading skeleton ── */
  if (fetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: single ? "center" : "flex-start",
          py: 6,
          ...(single
            ? { height: { xs: "auto", lg: "100%" }, minHeight: 0 }
            : {}),
        }}
      >
        <CircularProgress sx={{ color: "#2563EB" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={
        single
          ? {
              height: { xs: "auto", lg: "100%" },
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }
          : { display: "flex", flexDirection: "column", gap: SECTION_GAP }
      }
    >
      {/* ═══════════════ 1. GLOBAL MASTER ══════════════════════════ */}
      {show("general") && (
      <SectionCard
        icon={<TextFieldsIcon />}
        title="Global Master"
        accentColor="#2563EB"
        fill={single}
      >
        <Grid container spacing={2}>
          {/* Financial Year – unofficial only */}
          {isUnofficial && (
            <Grid item xs={12} sm={6} md={col(3.5, 12)}>
              {fyLoading ? (
                <CircularProgress size={22} sx={{ color: "#2563EB" }} />
              ) : (
                <FormControl fullWidth size="small" sx={tfSx}>
                  <InputLabel
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <CalendarMonthIcon sx={{ fontSize: 14 }} /> Financial Year
                  </InputLabel>
                  <Select
                    value={currentFyId ?? ""}
                    onChange={onFyChange}
                    label="Financial Year"
                    sx={{ borderRadius: "10px", bgcolor: "#fafbfc" }}
                  >
                    {fyOptions.map((fy) => (
                      <MenuItem key={fy.fy_id} value={fy.fy_id}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          FY {fy.fy_label}
                          {/* Marks the year the CALENDAR is in, which says
                              nothing about which year this branch has selected
                              — that is `currentFyId`, driving the Select's
                              value above. Labelled "Current" rather than
                              "Active" so the two cannot be read as the same
                              thing. Was `fy.is_active`, a stored flag that
                              conflated the two; migration 022 dropped it and
                              the server derives this from the date instead. */}
                          {calendarFyId != null && fy.fy_id === calendarFyId && (
                            <Chip
                              label="Current"
                              size="small"
                              sx={{
                                bgcolor: "#d1fae5",
                                color: "#065f46",
                                height: 18,
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                borderRadius: "5px",
                              }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Typography
                variant="caption"
                sx={{
                  color: "#94a3b8",
                  display: "block",
                  mt: 0.5,
                  fontSize: "0.7rem",
                }}
              >
                Selected FY is set as active app-wide
              </Typography>
            </Grid>
          )}

          {/* Master Interest */}
          <Grid item xs={6} sm={6} md={col(1.5, 4)}>
            <TextField
              fullWidth
              size="small"
              label="Interest"
              type="number"
              value={settings.master_int}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  onSettingsChange({ ...settings, master_int: 0 });
                  return;
                }
                const n = parseFloat(v);
                if (!isNaN(n))
                  onSettingsChange({ ...settings, master_int: clampRate(n) });
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <PercentIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                inputProps: { min: 0, max: MAX_MASTER_RATE, step: "any" },
              }}
              sx={tfSx}
            />
          </Grid>

          {/* Master Insurance */}
          <Grid item xs={6} sm={6} md={col(1.5, 4)}>
            <TextField
              fullWidth
              size="small"
              label="Insurance"
              type="number"
              value={settings.master_ins !== null ? settings.master_ins : ""}
              placeholder="Optional"
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  onSettingsChange({ ...settings, master_ins: null });
                  return;
                }
                const n = parseFloat(v);
                if (!isNaN(n))
                  onSettingsChange({ ...settings, master_ins: clampRate(n) });
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <PercentIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                inputProps: { min: 0, max: MAX_MASTER_RATE, step: "any" },
              }}
              sx={tfSx}
            />
          </Grid>

          {/* Prefix */}
          <Grid item xs={6} sm={6} md={col(2.5, 4)}>
            <TextField
              fullWidth
              size="small"
              label="Prefix (max 5 chars)"
              value={settings.prefix}
              onChange={(e) =>
                onSettingsChange({
                  ...settings,
                  prefix: e.target.value.toUpperCase().slice(0, 5),
                })
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <TagIcon sx={{ fontSize: 15, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
              sx={tfSx}
            />
          </Grid>

          {/* Use prefix checkbox */}
          <Grid
            item
            xs={6}
            sm={6}
            md={col(3, 6)}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <StyledCheck
              checked={settings.use_prefix || false}
              onChange={(e) =>
                onSettingsChange({ ...settings, use_prefix: e.target.checked })
              }
              label="Use prefix in ticket"
            />
            <InfoDot onClick={() => setInfoKey("use_prefix_for_ticket")} />
          </Grid>

          {/* OTP for customer creation checkbox */}
          <Grid
            item
            md={col(5.5, 6)}
            sm={6}
            xs={12}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <StyledCheck
              checked={settings.OTP_for_cust || false}
              onChange={(e) =>
                onSettingsChange({
                  ...settings,
                  OTP_for_cust: e.target.checked,
                })
              }
              label="OTP validation for customer creation"
            />
            <InfoDot onClick={() => setInfoKey("OTP_for_customer_creation")} />
          </Grid>

          {/* Allow duplicate mobile numbers checkbox */}
          <Grid
            item
            md={col(5.5, 6)}
            sm={6}
            xs={12}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <StyledCheck
              checked={settings.allow_duplicate_mobile || false}
              onChange={(e) =>
                onSettingsChange({
                  ...settings,
                  allow_duplicate_mobile: e.target.checked,
                })
              }
              label="Allow duplicate mobile numbers"
            />
            <InfoDot onClick={() => setInfoKey("allow_duplicate_mobile")} />
          </Grid>

          {/* Manual voucher number checkbox */}
          <Grid
            item
            md={col(3, 6)}
            sm={6}
            xs={12}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <StyledCheck
              checked={settings.manual_voucher_no || false}
              onChange={(e) =>
                onSettingsChange({
                  ...settings,
                  manual_voucher_no: e.target.checked,
                })
              }
              label="Manual voucher number"
            />
            <InfoDot onClick={() => setInfoKey("manual_voucher_no")} />
          </Grid>

          {prefixPeek && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.2,
                  p: 1.6,
                  borderRadius: "12px",
                  bgcolor: prefixPeek.isNewPrefix ? "#eff6ff" : "#fffbeb",
                  border: `1px solid ${
                    prefixPeek.isNewPrefix ? "#bfdbfe" : "#fde68a"
                  }`,
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: prefixPeek.isNewPrefix ? "#2563eb" : "#d97706",
                    mt: "1px",
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  {prefixPeek.prefix === "" ? (
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.83rem", color: "#374151" }}
                    >
                      Prefix is off — new tickets will be numbered{" "}
                      <strong>{prefixPeek.continueExample}</strong> (plain
                      number, no prefix).
                    </Typography>
                  ) : prefixPeek.isNewPrefix ? (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.83rem",
                          color: "#1e3a8a",
                          fontWeight: 600,
                          mb: 0.3,
                        }}
                      >
                        New prefix “{prefixPeek.prefix}” — choose how numbering
                        starts:
                      </Typography>
                      <RadioGroup
                        row
                        value={resetSequence ? "reset" : "continue"}
                        onChange={(e) =>
                          onResetSequenceChange(e.target.value === "reset")
                        }
                      >
                        <FormControlLabel
                          value="continue"
                          control={<Radio size="small" />}
                          label={
                            <Typography sx={{ fontSize: "0.82rem" }}>
                              Continue numbering →{" "}
                              <strong>{prefixPeek.continueExample}</strong>
                            </Typography>
                          }
                        />
                        {prefixPeek.canReset && (
                          <FormControlLabel
                            value="reset"
                            control={<Radio size="small" />}
                            label={
                              <Typography sx={{ fontSize: "0.82rem" }}>
                                Restart from 1 →{" "}
                                <strong>{prefixPeek.resetExample}</strong>
                              </Typography>
                            }
                          />
                        )}
                      </RadioGroup>
                    </>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.83rem", color: "#92400e" }}
                    >
                      Prefix “{prefixPeek.prefix}” was used before — numbering
                      will resume at <strong>{prefixPeek.resumeExample}</strong>
                      . Restart isn’t available for a prefix that already has
                      tickets.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </SectionCard>
      )}

      {/* ═══════════════ 2. ACCOUNT MAPPING ════════════════════════ */}
      {show("accounts") && (
      <SectionCard
        icon={<AccountBalanceIcon />}
        title="Account Mapping"
        accentColor="#0ea5e9"
        fill={single}
      >
        <Grid container spacing={2}>
          {[
            { field: "pledge_acc", label: "Pledge Account" },
            { field: "cash_acc", label: "Cash Account" },
            { field: "UPI_acc", label: "UPI Account" },
            { field: "interest_acc", label: "Interest Account" },
            { field: "insurance_acc", label: "Insurance Account" },
            { field: "other_charges_acc", label: "Other Charges Account" },
          ].map(({ field, label }) => (
            // Four across in the old full-width column; two across in a
            // quadrant, where four would squeeze each account name to nothing.
            <Grid item xs={12} sm={6} md={single ? 6 : 3} key={field}>
              <Autocomplete
                fullWidth
                size="small"
                options={getAccountOptions()}
                value={getSelectedAccount(settings[field])}
                onChange={handleAutocompleteChange(field)}
                loading={accountsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    sx={{
                      ...tfSx,
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                        { borderColor: "#7dd3fc" },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        { borderColor: "#0ea5e9" },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#0ea5e9" },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                noOptionsText="No accounts found"
              />
            </Grid>
          ))}
        </Grid>
      </SectionCard>
      )}

      {/* ═══════════════ 3. PAYMENT, RECEIPT & SETTLEMENT RULES ════════════════ */}
      {show("rules") && (
      <SectionCard
        icon={<CurrencyRupeeRoundedIcon />}
        title="Payment, Receipt & Settlement Rules"
        accentColor="#10b981"
        fill={single}
      >
        <Grid container spacing={2}>
          {/* Payment & Settlement — the two sub-columns stack in a quadrant,
              which is too narrow to put them side by side. */}
          <Grid item xs={12} sm={single ? 12 : 6} sx={{ display: "flex" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                // Side by side with the Receipt column, these two spread to
                // match its height. Stacked in a quadrant there is nothing to
                // match, so they sit at their natural height with a real gap —
                // space-between would strand them at opposite ends of a
                // scrolling panel.
                justifyContent: single ? "flex-start" : "space-between",
                width: "100%",
                height: single ? "auto" : "100%",
                gap: single ? 2 : { md: 0, xs: 2 },
              }}
            >
              {/* Payment sub-card */}
              <SectionCard
                icon={<PaymentsIcon />}
                title="Payment Settings"
                accentColor="#f59e0b"
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <StyledCheck
                    checked={settings.one_month_deduct}
                    onChange={(e) =>
                      onSettingsChange({
                        ...settings,
                        one_month_deduct: e.target.checked,
                      })
                    }
                    label="One month deduct"
                  />
                  <InfoDot onClick={() => setInfoKey("one_month_deduct")} />
                </Box>
              </SectionCard>

              {/* Settlement sub-card */}
              <SectionCard
                icon={<VerifiedRoundedIcon />}
                title="Settlement Settings"
                accentColor="#10b981"
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <StyledCheck
                    checked={settings.OTP_for_settlement || false}
                    onChange={(e) =>
                      onSettingsChange({
                        ...settings,
                        OTP_for_settlement: e.target.checked,
                      })
                    }
                    label="OTP validation for settlement"
                  />
                  <InfoDot onClick={() => setInfoKey("OTP_for_settlement")} />
                </Box>
              </SectionCard>
            </Box>
          </Grid>

          {/* Receipt sub-card */}
          <Grid item xs={12} sm={single ? 12 : 6}>
            <SectionCard
              icon={<ReceiptLongIcon />}
              title="Receipt Settings"
              accentColor="#2e0bf5"
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <StyledCheck
                    checked={settings.no_interest_upto_2_days}
                    onChange={(e) =>
                      onSettingsChange({
                        ...settings,
                        no_interest_upto_2_days: e.target.checked,
                      })
                    }
                    label="No interest within 2 days"
                  />
                  <InfoDot
                    onClick={() => setInfoKey("no_interest_upto_2_days")}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <StyledCheck
                    checked={settings.pawn_compound_interest}
                    onChange={(e) =>
                      onSettingsChange({
                        ...settings,
                        pawn_compound_interest: e.target.checked,
                      })
                    }
                    label="Pawn compound interest"
                  />
                  <InfoDot
                    onClick={() => setInfoKey("pawn_compound_interest")}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <StyledCheck
                    checked={settings.pawn_monthly_interest}
                    onChange={(e) => {
                      const isChecked = e.target.checked;

                      onSettingsChange({
                        ...settings,
                        pawn_monthly_interest: isChecked,

                        // Reset child when parent is unchecked
                        ...(isChecked
                          ? {}
                          : {
                              less_than_15_days_as_half_month: false,
                            }),
                      });
                    }}
                    label="Pawn monthly interest"
                  />

                  <InfoDot
                    onClick={() => setInfoKey("pawn_monthly_interest")}
                  />
                </Box>
                <Collapse in={settings.pawn_monthly_interest}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <StyledCheck
                      checked={settings.less_than_15_days_as_half_month}
                      onChange={(e) =>
                        onSettingsChange({
                          ...settings,
                          less_than_15_days_as_half_month: e.target.checked,
                        })
                      }
                      label="< 15 days = half month"
                    />

                    <InfoDot
                      onClick={() =>
                        setInfoKey("less_than_15_days_as_half_month")
                      }
                    />
                  </Box>
                </Collapse>
              </Box>
            </SectionCard>
          </Grid>
        </Grid>
      </SectionCard>
      )}

      {/* ═══════════ "Know more" detail dialog (shared) ════════════ */}
      <Dialog
        open={Boolean(info)}
        onClose={() => setInfoKey(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            fontSize: "1rem",
            color: "#1a1a2e",
            pb: 1,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 20, color: "#2563EB" }} />
          {info?.title}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: "#374151", lineHeight: 1.65, fontSize: "0.88rem" }}
          >
            {info?.description}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setInfoKey(null)}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#2563EB",
              boxShadow: "none",
              "&:hover": { bgcolor: "#1D4ED8", boxShadow: "none" },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PawnConfiguration;
